import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Avatar,
  Typography,
  Drawer,
  Input,
  Button,
  Space,
  Form,
  Image,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { getListCategories } from "services/category";
import { upload } from "services/upload";
import { createCategory } from "services/category";
import { updateCategory } from "services/category";

const { Title, Text } = Typography;

export default function ProductCategory() {
  const [categories, setCategories] = useState([]);
  const [visible, setVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(true);

  const showDrawer = (category = null) => {
    setSelectedCategory(category);
    setVisible(true);
    setIsEditing(category != null);
  };

  const onClose = () => {
    setSelectedCategory(null);
    form.resetFields();
    setFileList([]);
    setVisible(false);
  };

  const handleSubmit = async (values) => {
    if (isEditing) {
      await updateCategory({ ...values, id: selectedCategory.id });
    } else {
      await createCategory(values);
    }

    message.success("Category updated successfully!");
    fetchCategories();
    onClose();
  };

  useEffect(() => {
    if (selectedCategory) {
      form.setFieldsValue(selectedCategory);
      setFileList([]);
    }
  }, [selectedCategory, form]);

  const columns = [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      width: "90%",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
  ];

  const tableData = categories.map((category, index) => {
    const { name, image, id } = category;
    return {
      key: index,
      name: (
        <Avatar.Group>
          {/* <Avatar
            className="shape-avatar"
            src={image || "https://via.placeholder.com/40"}
            shape="square"
            size={40}
          /> */}
          <Image
            width={50}
            src={image || "https://via.placeholder.com/40"}
            alt="Category"
            style={{ borderRadius: 8 }}
          />
          <div className="avatar-info">
            <Title level={5} style={{ marginLeft: 12 }}>
              {name}
            </Title>
          </div>
        </Avatar.Group>
      ),
      action: (
        <a onClick={() => showDrawer(category)} key={`a-${id}`}>
          Edit
        </a>
      ),
    };
  });

  const fetchCategories = async () => {
    try {
      const response = await getListCategories();
      if (response?.result) {
        setCategories(response.result || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await upload(formData);

      const data = await response.json();
      if (data.status === "success") {
        const fullImageUrl = data.file_path;
        form.setFieldsValue({ image: fullImageUrl });
        setSelectedCategory({
          ...selectedCategory,
          image: fullImageUrl,
        });
        message.success("Image uploaded successfully!");
      } else {
        message.error("Upload failed!");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("Failed to upload image!");
    } finally {
      setUploading(false);
    }

    return false; // prevent auto upload
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Product Category"
              extra={
                <Space size="middle">
                  <Button type="primary" onClick={() => showDrawer()}>
                    Add Category
                  </Button>
                </Space>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      <Drawer
        title={
          <>
            Edit Category{" "}
            <Text className="subtitle">{selectedCategory?.name || ""}</Text>
          </>
        }
        width={400}
        visible={visible}
        onClose={onClose}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={selectedCategory}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter category name" }]}
          >
            <Input placeholder="Category name" />
          </Form.Item>

          <Form.Item label="Image" name="image">
            {selectedCategory?.image ? (
              <div style={{ textAlign: "center" }}>
                <Image
                  width={200}
                  src={selectedCategory.image}
                  alt="Category"
                  style={{ borderRadius: 8 }}
                />
                <div style={{ marginTop: 10 }}>
                  <Button
                    type="link"
                    onClick={() => {
                      form.setFieldsValue({ image: "" });
                      setSelectedCategory({
                        ...selectedCategory,
                        image: "",
                      });
                    }}
                  >
                    Change Image
                  </Button>
                </div>
              </div>
            ) : (
              <Upload
                listType="picture"
                fileList={fileList}
                beforeUpload={handleUpload}
                onChange={({ fileList }) => setFileList(fileList)}
                showUploadList={false}
                accept="image/*"
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Image"}
                </Button>
              </Upload>
            )}
          </Form.Item>

          <Form.Item>
            <Space style={{ display: "flex", justifyContent: "end" }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {isEditing ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}
